import { useState, useEffect } from "react";
import axios from "axios";
import {
  Settings,
  Brain,
  Check,
  X,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
  ExternalLink,
  Zap,
  Server,
  DollarSign,
  Activity,
  ChevronRight,
  Info,
  Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LLM_PROVIDERS = {
  emergent: {
    id: "emergent",
    name: "Emergent LLM",
    description: "Universal key for OpenAI, Anthropic, and Gemini models. Powers GPT-5.1, Claude, and more.",
    color: "from-teal-500 to-emerald-500",
    icon: Zap,
    models: [
      { id: "gpt-5.1", name: "GPT-5.1", description: "Latest OpenAI model via Emergent" },
      { id: "gpt-4o", name: "GPT-4o", description: "Fast and capable" },
      { id: "claude-sonnet-4.5", name: "Claude Sonnet 4.5", description: "Anthropic's latest" }
    ],
    docsUrl: "https://emergent.sh/docs",
    features: ["Universal API Key", "Multiple Models", "Auto-routing", "Cost Tracking"]
  },
  lemonfox: {
    id: "lemonfox",
    name: "LemonFox AI",
    description: "Open-source Llama models at incredibly low prices. $0.50-$1.25 per million tokens.",
    color: "from-yellow-500 to-orange-500",
    icon: Sparkles,
    models: [
      { id: "llama-8b-chat", name: "Llama 3.1 8B", description: "$0.50/1M tokens", price: 0.50 },
      { id: "llama-70b-chat", name: "Llama 3.3 70B", description: "$1.25/1M tokens", price: 1.25 }
    ],
    docsUrl: "https://lemonfox.ai/docs",
    features: ["OpenAI Compatible", "EU-based", "5x Cheaper", "Whisper STT"]
  },
  openai: {
    id: "openai",
    name: "OpenAI Direct",
    description: "Direct access to OpenAI API. Use your own OpenAI API key.",
    color: "from-purple-500 to-pink-500",
    icon: Brain,
    models: [
      { id: "gpt-4o", name: "GPT-4o", description: "Latest flagship model" },
      { id: "gpt-4o-mini", name: "GPT-4o Mini", description: "Fast and affordable" },
      { id: "gpt-4-turbo", name: "GPT-4 Turbo", description: "High performance" }
    ],
    docsUrl: "https://platform.openai.com/docs",
    features: ["Official API", "Latest Models", "Fine-tuning", "Assistants"]
  }
};

export default function LLMSettings() {
  const [config, setConfig] = useState({
    active_provider: "emergent",
    providers: {
      emergent: { enabled: true, api_key: "", model: "gpt-5.1" },
      lemonfox: { enabled: false, api_key: "", model: "llama-70b-chat" },
      openai: { enabled: false, api_key: "", model: "gpt-4o" }
    }
  });
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [activeTab, setActiveTab] = useState("providers");

  useEffect(() => {
    fetchConfig();
    fetchUsage();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/llm-config`);
      const data = response.data;
      
      // Merge with defaults
      const mergedProviders = { ...config.providers };
      Object.keys(data.providers || {}).forEach(key => {
        if (mergedProviders[key]) {
          mergedProviders[key] = { ...mergedProviders[key], ...data.providers[key] };
        }
      });
      
      setConfig({
        active_provider: data.active_provider || "emergent",
        providers: mergedProviders
      });
    } catch (error) {
      console.error("Failed to fetch LLM config:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/admin/llm-usage`);
      setUsage(response.data);
    } catch (error) {
      console.error("Failed to fetch LLM usage:", error);
    }
  };

  const handleProviderToggle = (providerId, enabled) => {
    setConfig(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [providerId]: {
          ...prev.providers[providerId],
          enabled
        }
      }
    }));
  };

  const handleApiKeyChange = (providerId, apiKey) => {
    setConfig(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [providerId]: {
          ...prev.providers[providerId],
          api_key: apiKey
        }
      }
    }));
  };

  const handleModelChange = (providerId, model) => {
    setConfig(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [providerId]: {
          ...prev.providers[providerId],
          model
        }
      }
    }));
  };

  const handleActiveProviderChange = (providerId) => {
    setConfig(prev => ({
      ...prev,
      active_provider: providerId
    }));
  };

  const saveConfig = async () => {
    // Validate active provider has API key
    const activeProviderConfig = config.providers[config.active_provider];
    if (!activeProviderConfig?.api_key && !activeProviderConfig?.api_key_configured) {
      toast.error(`Please configure API key for ${LLM_PROVIDERS[config.active_provider].name}`);
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/llm-config`, config);
      toast.success("LLM configuration saved successfully!");
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (providerId) => {
    const providerConfig = config.providers[providerId];
    const apiKey = providerConfig.api_key || "";
    
    if (!apiKey && !providerConfig.api_key_configured) {
      toast.error("Please enter an API key first");
      return;
    }

    setTesting(prev => ({ ...prev, [providerId]: true }));
    try {
      const response = await axios.post(`${API}/admin/llm-config/test`, null, {
        params: {
          provider: providerId,
          api_key: apiKey || "use_existing",
          model: providerConfig.model
        }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection test failed");
    } finally {
      setTesting(prev => ({ ...prev, [providerId]: false }));
    }
  };

  const toggleShowKey = (providerId) => {
    setShowKeys(prev => ({ ...prev, [providerId]: !prev[providerId] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="llm-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Brain className="w-8 h-8 text-primary" />
          LLM Configuration
        </h1>
        <p className="text-[#5C5C5C] mt-1">
          Configure AI providers for feedback generation, Q&A, and transcription
        </p>
      </div>

      {/* Active Provider Banner */}
      <Card className={`bg-gradient-to-r ${LLM_PROVIDERS[config.active_provider]?.color || 'from-teal-500 to-emerald-500'} text-white border-0`}>
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {(() => {
                const IconComponent = LLM_PROVIDERS[config.active_provider]?.icon || Zap;
                return <IconComponent className="w-12 h-12" />;
              })()}
              <div>
                <p className="text-sm opacity-80">Active Provider</p>
                <h2 className="text-2xl font-bold">{LLM_PROVIDERS[config.active_provider]?.name || "Not Set"}</h2>
                <p className="text-sm opacity-80 mt-1">
                  Model: {config.providers[config.active_provider]?.model || "Default"}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span className="font-medium">Connected</span>
              </div>
              {usage?.monthly && (
                <p className="text-sm opacity-80 mt-1">
                  {usage.monthly.total_calls || 0} calls this month
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-secondary/50 p-1 rounded-xl">
          <TabsTrigger value="providers" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Server className="w-4 h-4 mr-2" />
            Providers
          </TabsTrigger>
          <TabsTrigger value="usage" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Activity className="w-4 h-4 mr-2" />
            Usage & Stats
          </TabsTrigger>
        </TabsList>

        {/* Providers Tab */}
        <TabsContent value="providers" className="space-y-6">
          {/* Provider Cards */}
          {Object.entries(LLM_PROVIDERS).map(([providerId, provider]) => {
            const providerConfig = config.providers[providerId] || {};
            const isActive = config.active_provider === providerId;
            const isConfigured = providerConfig.api_key || providerConfig.api_key_configured;
            
            return (
              <Card 
                key={providerId} 
                className={`stats-card transition-all ${isActive ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                data-testid={`provider-card-${providerId}`}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${provider.color} flex items-center justify-center`}>
                        <provider.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {provider.name}
                          {isActive && (
                            <Badge className="bg-primary text-white">Active</Badge>
                          )}
                          {isConfigured && !isActive && (
                            <Badge variant="outline" className="text-[#059669] border-[#059669]">Configured</Badge>
                          )}
                        </CardTitle>
                        <CardDescription>{provider.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <a 
                        href={provider.docsUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline text-sm flex items-center gap-1"
                      >
                        Docs <ExternalLink className="w-3 h-3" />
                      </a>
                      <Switch
                        checked={providerConfig.enabled}
                        onCheckedChange={(checked) => handleProviderToggle(providerId, checked)}
                        data-testid={`toggle-${providerId}`}
                      />
                    </div>
                  </div>
                </CardHeader>

                {providerConfig.enabled && (
                  <CardContent className="space-y-4 pt-0">
                    {/* Features */}
                    <div className="flex flex-wrap gap-2">
                      {provider.features.map(feature => (
                        <span 
                          key={feature}
                          className="text-xs px-2 py-1 bg-secondary/50 rounded-full text-[#5C5C5C]"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      {/* API Key Input */}
                      <div className="space-y-2">
                        <Label htmlFor={`apikey-${providerId}`}>API Key</Label>
                        <div className="relative">
                          <Input
                            id={`apikey-${providerId}`}
                            type={showKeys[providerId] ? "text" : "password"}
                            placeholder={providerConfig.api_key_configured ? "••••••••••••" : "Enter your API key"}
                            value={providerConfig.api_key || ""}
                            onChange={(e) => handleApiKeyChange(providerId, e.target.value)}
                            className="pr-10"
                            data-testid={`input-apikey-${providerId}`}
                          />
                          <button
                            type="button"
                            onClick={() => toggleShowKey(providerId)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#5C5C5C]"
                          >
                            {showKeys[providerId] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {providerConfig.api_key_masked && !providerConfig.api_key && (
                          <p className="text-xs text-[#5C5C5C]">
                            Current: {providerConfig.api_key_masked}
                          </p>
                        )}
                      </div>

                      {/* Model Selection */}
                      <div className="space-y-2">
                        <Label>Model</Label>
                        <Select 
                          value={providerConfig.model || provider.models[0].id}
                          onValueChange={(value) => handleModelChange(providerId, value)}
                        >
                          <SelectTrigger data-testid={`select-model-${providerId}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {provider.models.map(model => (
                              <SelectItem key={model.id} value={model.id}>
                                <div className="flex items-center justify-between w-full">
                                  <span>{model.name}</span>
                                  <span className="text-xs text-[#9CA3AF] ml-2">{model.description}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => testConnection(providerId)}
                        disabled={testing[providerId]}
                        className="rounded-full"
                        data-testid={`test-${providerId}`}
                      >
                        {testing[providerId] ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Test Connection
                      </Button>
                      
                      {!isActive && isConfigured && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleActiveProviderChange(providerId)}
                          className="rounded-full"
                          data-testid={`activate-${providerId}`}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Set as Active
                        </Button>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={saveConfig} 
              disabled={saving}
              className="rounded-full px-6"
              data-testid="save-llm-config"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Configuration
            </Button>
          </div>
        </TabsContent>

        {/* Usage Tab */}
        <TabsContent value="usage" className="space-y-6">
          {/* Monthly Overview */}
          <Card className="stats-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Monthly Overview ({usage?.month || "Current"})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-secondary/50 rounded-xl">
                  <p className="text-3xl font-bold text-primary">
                    {(usage?.summary?.total_calls || usage?.monthly?.total_calls || 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-[#5C5C5C]">API Calls</p>
                </div>
                <div className="text-center p-4 bg-secondary/50 rounded-xl">
                  <p className="text-3xl font-bold text-primary">
                    {((usage?.summary?.total_tokens || usage?.monthly?.total_tokens || 0) / 1000).toFixed(1)}K
                  </p>
                  <p className="text-sm text-[#5C5C5C]">Tokens Used</p>
                </div>
                <div className="text-center p-4 bg-secondary/50 rounded-xl">
                  <p className="text-3xl font-bold text-primary">
                    ${(usage?.summary?.total_cost || usage?.monthly?.total_cost || 0).toFixed(2)}
                  </p>
                  <p className="text-sm text-[#5C5C5C]">Estimated Cost</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Usage by Operation */}
          <Card className="stats-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                Usage by Operation
              </CardTitle>
            </CardHeader>
            <CardContent>
              {usage?.by_operation && (Array.isArray(usage.by_operation) ? usage.by_operation.length > 0 : Object.keys(usage.by_operation).length > 0) ? (
                <div className="space-y-3">
                  {(Array.isArray(usage.by_operation) ? usage.by_operation : Object.entries(usage.by_operation).map(([op, data]) => ({operation: op, ...data}))).map((item, idx) => {
                    const operation = item.operation || item._id || `Operation ${idx}`;
                    const calls = item.count || item.calls || 0;
                    const tokens = item.total_tokens || item.tokens || 0;
                    const cost = item.total_cost || item.cost || 0;
                    return (
                      <div 
                        key={operation}
                        className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-primary" />
                          <span className="font-medium capitalize">{operation.replace(/_/g, ' ')}</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <span className="text-[#5C5C5C]">{calls} calls</span>
                          <span className="text-[#5C5C5C]">{(tokens / 1000).toFixed(1)}K tokens</span>
                          <span className="font-medium">${cost.toFixed(3)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-[#9CA3AF]">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No usage data yet</p>
                  <p className="text-sm">Start using AI features to see statistics</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cost Comparison */}
          <Card className="stats-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Info className="w-5 h-5 text-primary" />
                Provider Cost Comparison
              </CardTitle>
              <CardDescription>Estimated cost per 1 million tokens</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-orange-500" />
                    <span className="font-medium">LemonFox Llama 8B</span>
                  </div>
                  <span className="text-xl font-bold text-orange-600">$0.50</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-orange-500" />
                    <span className="font-medium">LemonFox Llama 70B</span>
                  </div>
                  <span className="text-xl font-bold text-orange-600">$1.25</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg border border-teal-200">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-teal-500" />
                    <span className="font-medium">Emergent GPT-5.1</span>
                  </div>
                  <span className="text-xl font-bold text-teal-600">~$2.50</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-3">
                    <Brain className="w-5 h-5 text-purple-500" />
                    <span className="font-medium">OpenAI GPT-4o</span>
                  </div>
                  <span className="text-xl font-bold text-purple-600">~$2.50</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
